import {
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  map,
  merge,
  shareReplay,
  type Observable,
} from 'rxjs';
import { ViewIds } from '@ardoq/api-types';
import type { PolarViewSettings } from '@ardoq/xyz-charts';
import { shallowEqual } from 'utils/objectUtils';
import { DEFAULT_DEBOUNCE_TIME } from 'modelInterface/consts';
import { toViewpointModeMainViewInstanceId } from 'appLayout/mainViewInstanceIds';
import { getViewpointModeViewSettingsWithChanges$ } from 'views/environmentals/viewSettings/viewpointModeViewSettings$';
import { loadedGraph$ } from 'traversals/loadedGraph$';
import { currentUserPermissionContext$ } from 'streams/currentUserPermissions/currentUserPermissionContext$';
import { locale$ } from 'streams/locale$';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { formattingAndLabelRules$ } from 'streams/filters/formattingAndLabelRules$';
import {
  toPlottedViewEffects$,
  toStableSelection$,
} from 'tabview/chartShared/plottedViewEffects';
import {
  buildPolarSourceData,
  type PolarSelection,
} from './sourceData/buildPolarSourceData';
import type { PolarViewProps } from './PolarView';

const VIEW_ID = ViewIds.POLAR;
const VIEW_INSTANCE_ID = toViewpointModeMainViewInstanceId(VIEW_ID);

const toPolarSelection = ({
  selectedFieldNameAngle,
  selectedFieldNameRadius,
  selectedFieldNameSeries,
}: PolarViewSettings): PolarSelection => ({
  selectedFieldNameAngle,
  selectedFieldNameRadius,
  selectedFieldNameSeries,
});

const viewSettings$ = getViewpointModeViewSettingsWithChanges$(
  VIEW_ID,
  VIEW_INSTANCE_ID
).pipe(map(({ currentSettings }) => currentSettings));

const sourceData$ = combineLatest({
  loadedGraph: loadedGraph$,
  permissionContext: currentUserPermissionContext$,
  locale: locale$,
  i18nLocale: i18nLocale$,
  formattingAndLabelRules: formattingAndLabelRules$,
  viewSettings: toStableSelection$(viewSettings$, toPolarSelection),
}).pipe(
  debounceTime(DEFAULT_DEBOUNCE_TIME),
  distinctUntilChanged(shallowEqual),
  map(args => buildPolarSourceData(VIEW_INSTANCE_ID, args)),
  shareReplay({ bufferSize: 1, refCount: true })
);

export const polarViewModel$: Observable<PolarViewProps> = merge(
  combineLatest([sourceData$, viewSettings$]).pipe(
    map(([sourceData, viewSettings]) => ({ ...sourceData, viewSettings }))
  ),
  toPlottedViewEffects$(sourceData$, VIEW_ID)
);
